/**
 * Privacy policy + account deletion (Sprint BO, DI-D rework).
 *
 * Two responsibilities on one screen:
 *
 *   1. Render the current privacy policy text. The body is
 *      sourced from `content/legal/privacy.ts` per active
 *      language — the page is dumb about prose and just
 *      iterates a typed structure (see `PolicyBody` below).
 *      Bumping the policy requires editing that file +
 *      bumping `POLICY_VERSION` here + the backend's
 *      `PDN_CURRENT_VERSION` constant (drift-guarded by
 *      `test_pdn_versions_match` on the backend side).
 *
 *   2. Show the logged-in user's consent + deletion state and
 *      drive the right-of-erasure flow:
 *        - Accept the current version (when `needs_consent_refresh`)
 *        - Request deletion (30-day grace)
 *        - Cancel deletion during the grace window
 *
 * Why this lives on one page instead of two: 152-ФЗ asks us
 * to surface the policy AND the data-subject's controls
 * together, so operators (and regulators auditing us) can hit
 * a single URL.
 */

import { useCallback, useEffect, useState } from "react";
import {
  AlertOctagon, BadgeCheck, Calendar, FileText, Loader2, ShieldCheck,
  Undo2, UserMinus,
} from "lucide-react";
import { Button, Card, CardHeader } from "@vseh/ui";

import { meApi, type MeDto } from "../shared/api";
import { useLang, useT } from "../shared/i18n";
import { ErrorBlock, LoadingBlock } from "../shared/components/States";
import {
  POLICY_CONTENT,
  type PolicyContent,
  type PolicyNode,
  type PolicySection,
} from "../content/legal/privacy";


/** Bump in lockstep with os-core/api/me.py:PDN_CURRENT_VERSION. */
const POLICY_VERSION = "2026-05-12";


export function PrivacyPage(): JSX.Element {
  const t = useT();
  const [me,    setMe]    = useState<MeDto | null>(null);
  const [err,   setErr]   = useState<Error | null>(null);
  const [busy,  setBusy]  = useState<"accept" | "delete" | "cancel" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setMe(await meApi.get());
    } catch (e) {
      setErr(e instanceof Error ? e : new Error(String(e)));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const accept = useCallback(async () => {
    setBusy("accept"); setNotice(null);
    try {
      const r = await meApi.acceptConsent();
      setNotice(r.message);
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [load]);

  const requestDeletion = useCallback(async () => {
    if (!confirm(t("privacy.confirm.delete"))) return;
    setBusy("delete"); setNotice(null);
    try {
      const r = await meApi.requestDeletion();
      setNotice(r.message);
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [load, t]);

  const cancelDeletion = useCallback(async () => {
    setBusy("cancel"); setNotice(null);
    try {
      const r = await meApi.cancelDeletion();
      setNotice(r.message);
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [load]);

  return (
    <div className="flex flex-col gap-4">

      {/* ----- Status card ------------------------------------------ */}
      <Card>
        <CardHeader
          title={t("privacy.page.title")}
          description={t("privacy.page.subtitle")}
        />
        {!me && !err && <LoadingBlock />}
        {err && <ErrorBlock error={err} onRetry={load} />}
        {me && <StatusBlock me={me} />}

        {notice && (
          <div className="mt-3 rounded-sm border border-base-200 bg-base-50 px-3 py-2 text-sm text-base-700">
            {notice}
          </div>
        )}

        {me && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {me.needs_consent_refresh && (
              <Button
                variant="primary" size="sm"
                leftIcon={<BadgeCheck className="size-4" />}
                disabled={busy !== null}
                onClick={accept}
              >
                {busy === "accept"
                  ? <Loader2 className="size-4 animate-spin" />
                  : t("privacy.action.accept")}
              </Button>
            )}
            {!me.deletion_requested_at && (
              <Button
                variant="ghost" size="sm"
                leftIcon={<UserMinus className="size-4 text-danger-600" />}
                disabled={busy !== null}
                onClick={requestDeletion}
              >
                {busy === "delete"
                  ? <Loader2 className="size-4 animate-spin" />
                  : t("privacy.action.delete")}
              </Button>
            )}
            {me.deletion_requested_at && (
              <Button
                variant="ghost" size="sm"
                leftIcon={<Undo2 className="size-4" />}
                disabled={busy !== null}
                onClick={cancelDeletion}
              >
                {busy === "cancel"
                  ? <Loader2 className="size-4 animate-spin" />
                  : t("privacy.action.cancel")}
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* ----- Policy body ------------------------------------------ */}
      <PolicyCard />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Status block — what consent / deletion state are you in
// ---------------------------------------------------------------------------


function StatusBlock({ me }: { me: MeDto }): JSX.Element {
  const t = useT();
  const { lang } = useLang();
  const acceptedDate = me.pdn_consent_accepted_at
    ? new Date(me.pdn_consent_accepted_at).toLocaleDateString(
        lang === "ru" ? "ru-RU" : "en-US",
      )
    : null;
  const deletionDate = me.deletion_requested_at
    ? new Date(me.deletion_requested_at)
    : null;
  const deletionFires = deletionDate
    ? new Date(deletionDate.getTime() + 30 * 24 * 3600 * 1000)
    : null;

  const consentSecondary =
    me.pdn_consent_version
      ? t("privacy.tile.consent.secondary", {
          version: me.pdn_consent_version,
          date:    acceptedDate ?? "",
        })
      : t("privacy.tile.consent.secondary.noversion");

  return (
    <div className="grid grid-cols-12 gap-3">
      <StatusTile
        icon={<ShieldCheck className={
          me.needs_consent_refresh ? "size-4 text-warning-600"
                                   : "size-4 text-success-600"
        } />}
        label={t("privacy.tile.consent")}
        primary={
          me.pdn_consent_accepted_at
            ? (me.needs_consent_refresh
                ? t("privacy.tile.consent.refresh")
                : t("privacy.tile.consent.active"))
            : t("privacy.tile.consent.none")
        }
        secondary={consentSecondary}
        tone={
          me.pdn_consent_accepted_at && !me.needs_consent_refresh ? "ok"
            : me.needs_consent_refresh                            ? "warn"
            : "danger"
        }
      />
      <StatusTile
        icon={<AlertOctagon className={
          deletionDate ? "size-4 text-danger-600" : "size-4 text-base-500"
        } />}
        label={t("privacy.tile.deletion")}
        primary={
          deletionDate
            ? t("privacy.tile.deletion.pending")
            : t("privacy.tile.deletion.none")
        }
        secondary={
          deletionFires
            ? t("privacy.tile.deletion.fires", {
                date: deletionFires.toLocaleDateString(
                  lang === "ru" ? "ru-RU" : "en-US",
                ),
              })
            : t("privacy.tile.deletion.empty")
        }
        tone={deletionDate ? "warn" : "ok"}
      />
      <StatusTile
        icon={<Calendar className="size-4 text-base-500" />}
        label={t("privacy.tile.version")}
        primary={POLICY_VERSION}
        secondary={t("privacy.tile.version.hint")}
      />
      <StatusTile
        icon={<BadgeCheck className="size-4 text-base-500" />}
        label={t("privacy.tile.email")}
        primary={me.email}
        secondary={
          me.email_verified_at
            ? t("privacy.tile.email.verified")
            : t("privacy.tile.email.unverified")
        }
      />
    </div>
  );
}


function StatusTile(props: {
  icon: JSX.Element;
  label: string;
  primary: string;
  secondary: string;
  tone?: "ok" | "warn" | "danger";
}): JSX.Element {
  const toneCls =
    props.tone === "danger" ? "text-danger-700" :
    props.tone === "warn"   ? "text-warning-700" :
                              "text-base-900";
  return (
    <div className="col-span-3 rounded-sm border border-base-200 bg-surface px-3 py-2">
      <div className="flex items-center gap-2 text-2xs font-medium uppercase tracking-wide text-base-500">
        {props.icon}{props.label}
      </div>
      <div className={`mt-1 truncate font-display text-sm font-semibold ${toneCls}`}>
        {props.primary}
      </div>
      <div className="truncate text-2xs text-base-500">{props.secondary}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Policy body — sourced from content/legal/privacy.ts per language.
// Bump POLICY_VERSION above AND the backend constant when the
// wording changes materially.
// ---------------------------------------------------------------------------


function PolicyCard(): JSX.Element {
  const { lang } = useLang();
  const content: PolicyContent = POLICY_CONTENT[lang];
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <FileText className="size-4 text-base-500" />
            {content.title}
          </span>
        }
        description={content.version.replace("{version}", POLICY_VERSION)}
      />
      <PolicyBody sections={content.sections} />
    </Card>
  );
}


function PolicyBody({ sections }: { sections: PolicySection[] }): JSX.Element {
  return (
    <article className="prose prose-sm max-w-none text-base-800">
      {sections.map((s) => (
        <section key={s.heading}>
          <H2>{s.heading}</H2>
          {s.body.map((node, i) => <NodeView key={i} node={node} />)}
        </section>
      ))}
    </article>
  );
}


function NodeView({ node }: { node: PolicyNode }): JSX.Element {
  if (node.kind === "p") return <P>{node.text}</P>;
  return <Ul items={node.items} />;
}


function H2({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h2 className="mt-5 font-display text-base font-semibold text-base-900">
      {children}
    </h2>
  );
}

function P({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="mt-2 text-sm leading-relaxed">{children}</p>;
}

function Ul({ items }: { items: string[] }): JSX.Element {
  return (
    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
      {items.map((it) => <li key={it}>{it}</li>)}
    </ul>
  );
}
